import '../../styles/_support-panel.scss';

import React, { useMemo } from 'react';

import { useGetBetaFeedbackPanelQuery } from '../../datasource/queries/cmsMessages';
import { SupportOptions } from '../../enums/SupportOptions';
import { useAppSelector } from '../../hooks';
import { useAnalytics } from '../../hooks/useAnalytics';
import Button from '../Button/Button';
import { ErrorContainer } from '../MainError/MainError';
import withPLIErrorBoundary from '../PliErrorBoundary/PliErrorBoundary';
import SmallPanel from '../SmallPanel/SmallPanel';
import BetaFeedback from './BetaFeedback';
import { ContactUs } from './ContactUs';
import { Debug } from './Debug';
import { PlayerDiagnostics } from './PlayerDiagnostics';
import { ReportAProblem } from './ReportAProblem';
import { supportItems } from './SupportPanel.utils';

export type ISupportPanelProps = {
    onClose(): void;
    title?: string;
    setSubPanel: (option: SupportOptions | false) => void;
    subPanel?: SupportOptions | false;
};

export const SupportPanelBase = ({
    onClose,
    title,
    setSubPanel,
    subPanel,
}: ISupportPanelProps) => {
    const clientConfig = useAppSelector((state) => state.clientConfig);
    const user = useAppSelector((state) => state.user);
    const launchDetails = useAppSelector((state) => state.player.launchDetails);
    const {
        data: betaFeedbackPanel
    } = useGetBetaFeedbackPanelQuery();
    const { pushToDataLayer } = useAnalytics();
    const canShowPlayerDiagnostics = !launchDetails.isWebcast;

    const showBetaFeedbackPanel = betaFeedbackPanel &&
        !!betaFeedbackPanel.supportPanelOptionLabel?.length &&
        !!betaFeedbackPanel.panelTitle?.length &&
        !!betaFeedbackPanel.panelContent?.length;

    const selectedSubPanel = useMemo(() => {
        if (subPanel === false) return null;

        if (subPanel === SupportOptions.PlayerDiagnostics && !canShowPlayerDiagnostics) {
            return null;
        }

        const subPanelItem = supportItems.find((item) => item.id === subPanel);

        switch (subPanel) {
            case SupportOptions.ReportAProblem: {
                return {
                    title: subPanelItem?.label,
                    component: () => (
                        <ReportAProblem
                            closeSubPanel={() => setSubPanel(false)}
                        />
                    ),
                };
            }
            case SupportOptions.SystemDiagnostics: {
                return {
                    title: subPanelItem?.label,
                    component: () => <div>This is the panel content.</div>,
                };
            }
            case SupportOptions.ContactUs: {
                return {
                    title: subPanelItem?.label,
                    component: ContactUs,
                };
            }
            case SupportOptions.PlayerDiagnostics: {
                return {
                    title: subPanelItem?.label,
                    component: PlayerDiagnostics,
                };
            }
            case SupportOptions.Debug: {
                return {
                    title: subPanelItem?.label,
                    component: Debug,
                };
            }
            case SupportOptions.BetaFeedback: {
                return {
                    title: betaFeedbackPanel?.panelTitle,
                    component: () => <BetaFeedback
                        panelTitle={betaFeedbackPanel?.panelTitle ?? ''}
                        panelContent={betaFeedbackPanel?.panelContent ?? ''}
                    />,
                };
            }
            default: {
                return {
                    title: subPanelItem?.label,
                    component: () => <div>This is the panel content.</div>,
                };
            }
        }
    }, [subPanel, setSubPanel, betaFeedbackPanel, canShowPlayerDiagnostics]);

    const handleClose = () => {
        setSubPanel(false);
        onClose();
    };

    const handleHelpCenterButtonClick =  () =>  {
        window.open(
            clientConfig!.pliHelpCenterUrl as string,
            '_blank'
        );
        pushToDataLayer({ event: 'visit_help' });
    };

    return (
        <>
            <SmallPanel
                isOpen={selectedSubPanel !== null}
                onBack={() => setSubPanel(false)}
                onClose={handleClose}
                title={''}
                isSublevel={true}
                hasStickyButton={true}
            >
                {selectedSubPanel && <selectedSubPanel.component />}
            </SmallPanel>
            <>
                <h3 className="support-panel__title heading-5">{title}</h3>
                <div className="support-panel__button-container">
                    {supportItems
                        .filter(
                            (item) =>
                                (item.id !== SupportOptions.SystemDiagnostics && //remove SystemDiagnostics temporarily
                                (item.id !== SupportOptions.PlayerDiagnostics || canShowPlayerDiagnostics) &&
                                (item.id !== SupportOptions.Debug || user.isPliEmployee) &&
                                (item.id !== SupportOptions.BetaFeedback || showBetaFeedbackPanel))
                        )
                        .map((supportItem) => {
                            return (
                                <Button
                                    key={supportItem.id}
                                    icon='forward-arrow'
                                    iconPosition='right'
                                    label={supportItem.id === SupportOptions.BetaFeedback ? betaFeedbackPanel?.supportPanelOptionLabel ?? supportItem.label : supportItem.label}
                                    buttonClass="support-panel__button"
                                    action={() => setSubPanel(supportItem.id)}
                                />
                            );
                        })}
                </div>
                <Button
                    icon='outbound-link'
                    iconPosition='right'
                    iconClass='ml-2'
                    label='Visit the PLI Help Center'
                    aria-label='Visit the PLI Help Center in a new tab'
                    buttonClass='support-panel__button--ghost paragraph-1--bold'
                    action={handleHelpCenterButtonClick}
                />
            </>
        </>
    );
};


/**
 * Default export: component already wrapped with the Error Boundary HOC.
 * - Uses MainError as fallback (SmallPanel layout).
 * - Renders only the error page without other app components.
 */
export const SupportPanel = withPLIErrorBoundary(SupportPanelBase, null, {
    errorContainer: ErrorContainer.SmallPanel,
    title: 'Sorry, we can\'t load the support panel.',
});

export default SupportPanel;
